import { Box, Stack } from '@chakra-ui/react';
import { Card } from '@main/complykit/components/card';
import { FieldCaption, RequiredMark } from '@main/complykit/components/field';
import { Content, Header } from '@main/complykit/components/slots';
import { Text } from '@main/complykit/components/text';
import { QuestionnaireComponentKind, QuestionnaireFormComponentRegistry } from '@main/dynamic-form';
import { DynamicFieldWrapper } from '@main/dynamic-form/react';
import { Vendor_Questionnaire_Form_Answer_Statuses_Enum } from '@main/graphql/types.generated';
import { useEffectOnce } from '@main/shared/browser';
import { MarkdownEditor } from '@main/ui';
import { SetStateAction, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FieldCollapsedHeader } from './field-collapsed-header';
import { QuestionnaireFieldComment } from './field-comment';
import { QuestionnaireFieldFiles } from './field-files';
import { QuestionnaireFieldHeader } from './field-header';
import { QuestionnaireFieldSupportingFile } from './field-supporting-file';
import {
  QuestionnaireFormFieldConfig,
  QuestionnnaireFieldWrapperProps,
  QuestionnnaireFormMode,
} from './form-types';
import { createNewAnswerFor } from './utils';

export const QuestionnnaireFieldWrapper: DynamicFieldWrapper<
  QuestionnaireFormComponentRegistry,
  QuestionnnaireFieldWrapperProps
> = (props) => {
  if (props.config.kind === QuestionnaireComponentKind.Section) {
    return props.children;
  }

  return <FieldWrapper {...props} />;
};

QuestionnnaireFieldWrapper.resolveState = (config, formState, props) => {
  if (config.kind === QuestionnaireComponentKind.Section) {
    return formState;
  }

  const answer = props?.form.answers?.find((answer) => answer.field_name === config.name);
  const isAnswerMode = props?.form.mode === QuestionnnaireFormMode.Answer;

  if (!isAnswerMode || !answer) {
    return formState;
  }

  return {
    ...formState,
    isReadOnly:
      !!formState.isReadOnly ||
      answer.status === Vendor_Questionnaire_Form_Answer_Statuses_Enum.Completed,
  };
};

const FieldWrapper: DynamicFieldWrapper<
  QuestionnaireFormComponentRegistry,
  QuestionnnaireFieldWrapperProps
> = (props) => {
  const { t } = useTranslation();

  const config = props.config;

  const answer = useMemo(
    () =>
      props.form.answers?.find((answer) => answer.field_name === config.name) ??
      createNewAnswerFor(config),
    [config, props.form.answers],
  );
  const supportingFile = useMemo(
    () =>
      config.supportingUploadId
        ? props.form.uploads?.find((upload) => upload.id === config.supportingUploadId)
        : undefined,
    [config.supportingUploadId, props.form.uploads],
  );
  const hasComment = !!answer.comment;
  const hasFiles = !!answer.support_files?.length;
  const { state, labelId } = props;

  const [wasShowingComment, setWasShowingComment] = useState(false);
  const [wasShowingFiles, setWasShowingFiles] = useState(false);
  const [isShowComment, _setShowComment] = useState(wasShowingComment || hasComment);
  const [isShowFiles, _setShowFiles] = useState(wasShowingFiles || hasFiles);

  const setShowComment = (setter: SetStateAction<boolean>) => {
    _setShowComment(setter);
    setWasShowingComment(setter);
  };

  const setShowFiles = (setter: SetStateAction<boolean>) => {
    _setShowFiles(setter);
    setWasShowingFiles(setter);
  };

  useEffectOnce(() => {
    if (hasComment) {
      setWasShowingComment(true);
    }

    if (hasFiles) {
      setWasShowingFiles(true);
    }
  });

  useEffect(() => {
    if (answer.status !== Vendor_Questionnaire_Form_Answer_Statuses_Enum.Completed) {
      return;
    }

    setShowComment(hasComment);
    setShowFiles(hasFiles);
  }, [answer.status, hasComment, hasFiles]);

  if (props.isCollapsed) {
    /* The answer stays mounted but hidden, so it keeps its testid without
     * taking space. It sits inside the header rather than its own slot: a
     * slot would add its inset around nothing, which is height a collapsed
     * card should not have. */
    return (
      <Card padding="compact" data-testid={`question:${config.label}`}>
        <Header>
          <FieldCollapsedHeader
            {...props}
            config={config}
            answer={answer}
            isRequired={state.isRequired}
            isInvalid={!!state.error}
          />
          <Box display="none" aria-hidden data-testid={`answer:${config.label}`}>
            {props.children}
          </Box>
        </Header>
      </Card>
    );
  }

  return (
    <Card data-testid={`question:${config.label}`}>
      <Header>
        <Stack>
          <QuestionnaireFieldHeader
            {...props}
            config={config}
            answer={answer}
            setShowComment={setShowComment}
            setShowFiles={setShowFiles}
          />
          <Text id={labelId} size="sm" weight="medium">
            {config.label}
            {state.isRequired && <RequiredMark announces={!controlCarriesRequired(config)} />}
          </Text>
          {config.showDescription && config.description && (
            <MarkdownEditor
              variant="unstyled"
              config={{ editable: false }}
              value={config.description}
              boxProps={{ color: 'gray.500' }}
              contentEditableProps={{ style: { fontSize: 'var(--chakra-fontSizes-sm)' } }}
            />
          )}
        </Stack>
      </Header>
      <Content>
        <Stack>
          {supportingFile && <QuestionnaireFieldSupportingFile supportingFile={supportingFile} />}
          <FieldCaption>{t('questionnaires.form.answerLabel')}</FieldCaption>
          <div data-testid={`answer:${config.label}`}>{props.children}</div>
          {isShowComment && (
            <QuestionnaireFieldComment {...props} config={config} answer={answer} />
          )}
          {isShowFiles && <QuestionnaireFieldFiles {...props} config={config} answer={answer} />}
        </Stack>
      </Content>
    </Card>
  );
};

/** Whether the answer's control announces required itself; a group of
 * checkboxes and a file input cannot, so the question's mark is read instead
 * (field.md, "Required, disabled, read-only"). */
function controlCarriesRequired(config: QuestionnaireFormFieldConfig) {
  if (config.kind === QuestionnaireComponentKind.File) {
    return false;
  }
  return !(config.kind === QuestionnaireComponentKind.Options && config.isMultiOptions);
}
